/**
 * HIS V4 · presence REST client (เขียนมือเอง — ยังไม่ regen issara-sdk)
 *
 * เหตุผลที่ไม่ใช้ BaseService:
 *   BaseService มี interceptor ที่เปิด modal ทุกครั้งที่ POST ตอบ 500
 *   traffic ของ presence เป็น background (heartbeat/ppoll ทุก 10-15 วิ) ไม่ควรเด้ง modal ให้ผู้ใช้
 *   จึงใช้ axios ตรง ๆ และคืน result แบบ {ok, error} ให้ caller ตัดสินใจเอง
 *
 * endpoint (his/core/views/presence.py):
 *   POST /apis/core/presence/join/      เข้าหน้า + คืน peers ทั้งหมด
 *   POST /apis/core/presence/heartbeat/ ต่ออายุ + แจ้ง state/field
 *   POST /apis/core/presence/leave/     ออกทันที
 *   GET  /apis/core/presence/peers/     snapshot (REST polling fallback)
 */

import axios, { AxiosInstance } from "axios";
import config from "config/config";

import type { PresenceConfig, PresencePeersResult, PresenceStateValue } from "./presenceTypesV4";

const BASE_PATH = `${config.API_HOST}/apis/core/presence`;
const REQUEST_TIMEOUT_MS = 8000;

export type PresencePath = "join" | "heartbeat" | "leave" | "peers";

/** ต้องมี / ครบทั้งสองข้าง (เคยพลาดได้ /apis/core/presence + "heartbeat/" = presenceheartbeat/ -> 404) */
export const PRESENCE_PATHS: Record<PresencePath, string> = {
  join: "/join/",
  heartbeat: "/heartbeat/",
  leave: "/leave/",
  peers: "/peers/",
};

/** ประกอบ URL แบบ pure function (test ได้โดยไม่ต้องโหลด config) */
export function presenceUrlOf(basePath: string, path: PresencePath): string {
  const base = String(basePath || "").replace(/\/+$/, "");
  return `${base}${PRESENCE_PATHS[path]}`;
}

export type PresenceRequest = {
  apiToken?: string | null;
  divisionId: number;
  pageKey: string;
  clientId: string;
  state?: PresenceStateValue;
  field?: string | null;
  meta?: Record<string, unknown>;
};

export type PresenceResult<T> = {
  ok: boolean;
  data?: T;
  /** 401 = token หมดอายุ, 400 = page_key/client_id ใช้ไม่ได้ */
  status?: number;
  error?: string;
  /** backend ตอบ {enabled:false} เมื่อ feature ถูกปิด -> client ต้องหยุดเงียบ */
  disabled?: boolean;
};

let instance: AxiosInstance | null = null;

const clientOf = (): AxiosInstance => {
  if (!instance) {
    instance = axios.create({
      timeout: REQUEST_TIMEOUT_MS,
      withCredentials: true,
      xsrfCookieName: "csrftoken",
      xsrfHeaderName: "X-CSRFToken",
    });
  }
  return instance;
};

const headersOf = (request: PresenceRequest) => ({
  ...(request.apiToken ? { Authorization: `Token ${request.apiToken}` } : {}),
  /* DeviceDivisionMiddleware อ่าน header "division" (ส่งไว้กัน case ที่ body ไม่มี division_id) */
  division: String(request.divisionId),
});

const bodyOf = (request: PresenceRequest) => ({
  page_key: request.pageKey,
  client_id: request.clientId,
  division_id: request.divisionId,
  state: request.state,
  field: request.field ?? null,
  meta: request.meta,
});

/**
 * backend ยังไม่ deploy presence endpoint (site เก่ายังไม่มี his/core/presence ในโค้ด)
 * หรือ proxy ตอบ 404 เป็น HTML -> presence ต้องปิดตัวเองทั้ง session ไม่ใช่ยิง API ที่ไม่มีความหมาย
 */
export const ENDPOINT_MISSING_STATUSES = [404, 405, 410];

export function isEndpointMissing(status: number | undefined, data: unknown): boolean {
  if (status !== undefined && ENDPOINT_MISSING_STATUSES.indexOf(status) >= 0) return true;
  const text = typeof data === "string" ? data : "";
  return /<!doctype html|<html[\s>]|<head[\s>]|404 Not Found|Page not found|Cannot resolve url/i.test(text);
}

const dataOf = (error: unknown): unknown =>
  (error as { response?: { data?: unknown } })?.response?.data;

const errorTextOf = (error: unknown): string => {
  const err = error as { message?: string; response?: { status?: number; data?: unknown } };
  const data = err?.response?.data;
  if (typeof data === "string") return data.slice(0, 200);
  if (data && typeof data === "object" && "detail" in data) return String((data as { detail: unknown }).detail);
  return err?.message || "network error";
};

function unwrap<T>(response: { data?: T; status?: number }, error?: unknown): PresenceResult<T> {
  if (error) {
    const status = statusOf(error);
    return {
      ok: false,
      error: errorTextOf(error),
      /* 404/405/410 หรือหน้า HTML = endpoint ไม่มีจริง -> disabled ให้ client หยุดทุกอย่าง */
      disabled: isEndpointMissing(status, dataOf(error)),
      status,
    };
  }
  const raw = response.data as (T & { enabled?: boolean }) | undefined;
  if (typeof raw === "string") {
    /* โดเมนที่ยังไม่ deploy บางที proxy ตอบ 200 พร้อม HTML — อย่าตีความเป็น peers */
    return { ok: false, error: "response ไม่ใช่ JSON", disabled: isEndpointMissing(response.status, raw), status: response.status };
  }
  if (raw && raw.enabled === false) return { ok: true, data: raw, disabled: true, status: response.status };
  return { ok: true, data: raw, status: response.status };
}

async function callApi<T>(
  method: "get" | "post",
  path: PresencePath,
  req: PresenceRequest,
  body?: unknown,
): Promise<PresenceResult<T>> {
  try {
    const response = await clientOf().request<T>({
      method,
      url: presenceUrlOf(BASE_PATH, path),
      headers: headersOf(req),
      data: body,
      params: method === "get" ? { page_key: req.pageKey, division_id: req.divisionId } : undefined,
    });
    return unwrap<T>(response);
  } catch (error) {
    return unwrap<T>({}, error);
  }
}

export const joinPresence = (req: PresenceRequest) =>
  callApi<PresencePeersResult>("post", "join", req, bodyOf(req));

export const heartbeatPresence = (req: PresenceRequest) =>
  callApi<{ ok: boolean; changed?: boolean; rev?: number; cfg?: PresenceConfig }>(
    "post",
    "heartbeat",
    req,
    bodyOf(req),
  );

export const leavePresence = (req: PresenceRequest) =>
  callApi<{ ok: boolean; removed?: string }>("post", "leave", req, bodyOf(req));

export const listPeers = (req: PresenceRequest) => callApi<PresencePeersResult>("get", "peers", req);

/**
 * ตอนปิด tab: fetch + keepalive (axios ไม่รับประกันว่า request จะรอดหลัง page unload)
 * ถ้าไม่เรียกเลย backend จะลบเองเมื่อ TTL ครบ
 */
const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export function leavePresenceKeepalive(req: PresenceRequest): void {
  try {
    if (typeof fetch !== "function") return;
    void fetch(presenceUrlOf(BASE_PATH, "leave"), {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: { "content-type": "application/json", ...headersOf(req) },
      body: JSON.stringify(bodyOf(req)),
    }).catch(() => undefined);
  } catch (error) {
    /* ปิด tab แล้วเงียบเป็น */
  }
}

/** ตัวที่ inject เข้า PresenceClientV4 ได้ (test ใส่ fake ได้โดยไม่ต้อง jest.mock) */
export type PresenceApiV4 = {
  join: typeof joinPresence;
  heartbeat: typeof heartbeatPresence;
  leave: typeof leavePresence;
  listPeers: typeof listPeers;
  leaveKeepalive: typeof leavePresenceKeepalive;
};

export const presenceApiV4: PresenceApiV4 = {
  join: joinPresence,
  heartbeat: heartbeatPresence,
  leave: leavePresence,
  listPeers: listPeers,
  leaveKeepalive: leavePresenceKeepalive,
};
